import { useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { createBanner } from 'services/cm-api/create-banner'
import { BannerT, WebApi2Response } from 'types/global'

/**
 * Wraps `POST /banner`. CMAPI returns 400 when:
 *  - `target_all` is false and all three audience arrays are empty
 *  - `message_en` or `message_da` is blank
 *  - `starts_at >= ends_at`
 *
 * Each surfaces here as a snackbar so the operator sees the
 * server's message verbatim. On success we invalidate the list.
 */
export function useCreateBanner() {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(createBanner, {
    onSuccess: async (response: WebApi2Response<BannerT>) => {
      if (response.success) {
        enqueueSnackbar('Banner created', { variant: 'success' })
        await queryClient.invalidateQueries(['getBanners'])
      }
    },
    onError: (err: unknown) => {
      const axiosErr = err as {
        response?: { data?: { error?: { message?: string } } }
      }
      const apiMessage = axiosErr?.response?.data?.error?.message
      enqueueSnackbar(apiMessage ?? 'Failed to create banner', {
        variant: 'error',
      })
      console.error('createBanner failed: ', err)
    },
  })
}
